import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FadeIn } from './FadeIn';
import { useLanguage } from './LanguageContext';
import { NavBar } from './NavBar';
import { AboutPage } from './AboutPage';
import { FounderPage } from './FounderPage';
import { ServicePage } from './ServicePage';
import { AIChatWidget } from './AIChatWidget';
import { AIChatPage } from './AIChatPage';
import nadyHololArabicVideo from './media/nadyHololArabic.mp4';
import hololClubEnglishVideo from './media/hololclubEnglish.mp4';
import nadyhololLogo from './media/images/hololconsulting.png';

function App() {
  const location = useLocation();
  const { messages, t, toggleLanguage, language } = useLanguage();
  const [regTab, setRegTab] = useState('partner');
  const [regFormStep, setRegFormStep] = useState(0);
  const [submitState, setSubmitState] = useState({ status: 'idle', message: '' });
  const registrationStepTotal = 7;
  const registrationLastStep = registrationStepTotal - 1; // 0..6
  const partnerCardsRef = useRef(null);
  const [partnerCarouselSlide, setPartnerCarouselSlide] = useState(0);
  const [partnerCarouselPaused, setPartnerCarouselPaused] = useState(false);
  const powerPillars = messages.partnersBanner.powerPillars;
  const successPartners = messages.partnersBanner.successPartners;

  useEffect(() => {
    setPartnerCarouselSlide(0);
  }, [language]);

  useEffect(() => {
    setRegFormStep(0);
    setSubmitState({ status: 'idle', message: '' });
  }, [regTab, language]);

  useEffect(() => {
    if (partnerCarouselPaused) return undefined;
    const id = window.setInterval(() => {
      setPartnerCarouselSlide((s) => (s + 1) % 2);
    }, 5000);
    return () => window.clearInterval(id);
  }, [language, partnerCarouselPaused]);

  /** Scroll to #hash on home (e.g. /#joining-benefits) after render — matches fixed nav offset */
  useEffect(() => {
    if (location.pathname !== '/') return;
    const raw = location.hash?.replace(/^#/, '') ?? '';
    if (!raw) return;
    const el = document.getElementById(raw);
    if (!el) return;
    const NAV_OFFSET = 96;
    requestAnimationFrame(() => {
      const y = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
      window.scrollTo({ top: Math.max(0, y), behavior: 'auto' });
    });
  }, [location.pathname, location.hash]);
  const staticFormsEndpoint =
    process.env.REACT_APP_STATICFORMS_ENDPOINT || 'https://api.staticforms.xyz/submit';
  const staticFormsAccessKey = process.env.REACT_APP_STATICFORMS_ACCESS_KEY;

  const serviceIcons = messages.serviceIcons;
  const partnerAds = messages.partnerAds;
  const agentBenefits = messages.agentBenefits;
  const serviceCardImages = [
    'https://uasupporters.com/wp-content/uploads/2023/03/Legal-Service-Firms-Top-Banner-1.jpg', // legal
    'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1400&q=80', // accounting
    'https://images.unsplash.com/photo-1559526324-593bc073d938?auto=format&fit=crop&w=1400&q=80', // financing
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=80', // feasibility
    'https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?auto=format&fit=crop&w=1400&q=80', // marketing
    'https://images.unsplash.com/photo-1529074963764-98f45c47344b?auto=format&fit=crop&w=1400&q=80', // immigration
  ];
  const pdfPages = Array.from({ length: 13 }, (_, i) => {
    const page = i + 1;
    const localeFolder = language === 'ar' ? 'pages-ar' : 'pages-en';
    return {
      page,
      src: `/media/pdfs/${localeFolder}/page-${String(page).padStart(2, '0')}.png`,
    };
  });
  const scrollPartnerCards = (direction) => {
    const el = partnerCardsRef.current;
    if (!el) return;
    const amount = Math.max(el.clientWidth * 0.85, 320);
    const effectiveDirection =
      language === 'ar' ? (direction === 'right' ? 'left' : 'right') : direction;
    const base = effectiveDirection === 'right' ? amount : -amount;
    const left = language === 'ar' ? -base : base;
    el.scrollBy({ left, behavior: 'smooth' });
  };
  const displayedPdfPages = pdfPages;
  const NAV_SCROLL_OFFSET = 96;
  const scrollToRegistration = () => {
    const section = document.getElementById('registration');
    if (!section) return;
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const scrollToSectionById = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - NAV_SCROLL_OFFSET;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  };

  const heroStatCards = [
    {
      title: t('hero.statPartners'),
      image:
        'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=80',
      initial:
        'top-8 left-8 rotate-[-9deg] group-hover:top-0 group-hover:left-0 group-hover:rotate-0',
    },
    {
      title: t('hero.statOffices'),
      image:
        'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80',
      initial:
        'top-10 right-8 rotate-[10deg] group-hover:top-0 group-hover:right-0 group-hover:rotate-0',
    },
    {
      title: t('hero.statSuccess'),
      image:
        'https://images.unsplash.com/photo-1556155092-490a1ba16284?auto=format&fit=crop&w=1200&q=80',
      initial:
        'bottom-10 left-10 rotate-[8deg] group-hover:bottom-0 group-hover:left-0 group-hover:rotate-0',
    },
    {
      title: t('hero.statCapital'),
      image:
        'https://images.unsplash.com/photo-1559526324-593bc073d938?auto=format&fit=crop&w=1200&q=80',
      initial:
        'bottom-8 right-10 rotate-[-8deg] group-hover:bottom-0 group-hover:right-0 group-hover:rotate-0',
    },
  ];
  const isBenefitSubheading = (text) =>
    /^(أولاً|ثانياً|ثالثاً|رابعاً|خامساً|First|Second|Third|Fourth|Fifth)\s*:/.test(text.trim());
  const registrationStepLabel = t('registration.formStep')
    .replace(/\{\{n\}\}/g, String(regFormStep + 1))
    .replace(/\{\{t\}\}/g, String(registrationStepTotal));
  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    if (regFormStep !== registrationLastStep) {
      return;
    }
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (!staticFormsAccessKey) {
      setSubmitState({ status: 'error', message: t('registration.missingEndpoint') });
      return;
    }

    const formData = new FormData(form);
    formData.append('accessKey', staticFormsAccessKey);
    formData.append('applicationType', regTab === 'partner' ? 'agent' : 'service_provider');
    formData.append('language', language);

    setSubmitState({ status: 'submitting', message: '' });
    try {
      const response = await fetch(staticFormsEndpoint, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const payload = await response.json().catch(() => null);
      if (response.ok && payload?.success) {
        setSubmitState({ status: 'success', message: t('registration.success') });
        form.reset();
        return;
      }

      setSubmitState({ status: 'error', message: t('registration.error') });
    } catch {
      setSubmitState({ status: 'error', message: t('registration.error') });
    }
  };

  if (location.pathname === '/ai-assistant') {
    return <AIChatPage />;
  }
  if (location.pathname === '/about-us') {
    return <AboutPage />;
  }
  if (location.pathname === '/founder') {
    return <FounderPage />;
  }
  if (location.pathname.startsWith('/services/')) {
    const slug = location.pathname.replace('/services/', '').replace(/\/$/, '');
    return <ServicePage slug={slug} />;
  }

  return (
    <>
      <NavBar t={t} language={language} toggleLanguage={toggleLanguage} />

      {/* Hero Section */}
      <header className="relative min-h-screen flex items-start sm:items-center pt-20 pb-12 overflow-hidden bg-primary">
        <div className="absolute inset-0 z-0">
          <img
            className="w-full h-full object-cover opacity-40"
            alt={t('hero.imgAlt')}
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuDThrhbnN5YjExpoeyjkSuyYTj9c1a0tsY1P4cMT5Uh5c0tq2pOBIHcQ0C_Q5-cgZ7dgFHgSTzB3y20FfN65XCkG0Akmk-HBMWtZ4UdncfT7KL6fHPUdQa_ecEmB3ABQ0wO2XMoro4RN_1KpvvJYyw0HChZB8X-Ds3YtlgaUWXYZr5cwKY5auB0sjFX3ICzMMBlRt1zHpe8CGN9Lmz4DrO2y-JZwySG4Pmj-3T0yhu1p7LPPw8q30DL9TL_AP3FTL1CTYGo0h7weA0"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-primary via-primary/80 to-transparent rtl:bg-gradient-to-tl" />
        </div>
        <div className="relative z-10 max-w-screen-2xl mx-auto px-4 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-12 items-center">
          <div className="space-y-8">
            <FadeIn mode="mount" delay={100}>
              <div className="flex items-center gap-5 mt-4 mb-6">
                <img
                  src={nadyhololLogo}
                  alt={t('hero.imgAlt')}
                  className="w-16 h-16 sm:w-24 sm:h-24 object-contain shrink-0"
                />
                <p className="text-lg sm:text-xl font-semibold text-white/85 leading-relaxed max-w-3xl">
                  {t('hero.slogan')}
                </p>
              </div>
            </FadeIn>
            <FadeIn mode="mount" delay={120}>
              <h1 className="font-headline font-extrabold text-white leading-[1.15] tracking-tight">
                <span className="block text-3xl sm:text-5xl lg:text-6xl">{t('hero.title1')}</span>
                <span className="mt-8 block text-1xl sm:text-1xl lg:text-1xl text-secondary no-balance">{t('hero.title2')}</span>
              </h1>
            </FadeIn>
            <FadeIn mode="mount" delay={220}>
              <p className="hero-mobile-natural text-xl font-bold text-white/70 max-w-3xl leading-relaxed">{t('hero.subtitle')}</p>
            </FadeIn>
            <FadeIn mode="mount" delay={320}>
              <div className="flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={scrollToRegistration}
                  className="px-8 py-4 bg-secondary text-white rounded-xl font-bold text-lg hover:shadow-[0_0_40px_-10px_rgba(119,90,25,0.5)] transition-all"
                >
                  {t('hero.ctaPartner')}
                </button>
                <a
                  className="px-8 py-4 bg-white/5 border border-white/20 text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all backdrop-blur-md inline-flex items-center"
                  href="https://www.adhamelsharkawy.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('hero.ctaProvider')}
                </a>
                <a
                  className="px-8 py-4 bg-white/5 border border-white/20 text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all backdrop-blur-md inline-flex items-center"
                  href="https://www.hololrealestate.club"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('hero.ctaLatestProjects')}
                </a>
              </div>
            </FadeIn>

            {/* Mobile hero imagery */}
            <FadeIn mode="mount" delay={380} className="lg:hidden">
              <div className="relative mx-auto w-full max-w-xl h-[360px]">
                <div className="group relative w-full h-full">
                  <div className="aspect-square bg-gradient-to-br from-secondary/20 to-transparent rounded-full blur-3xl absolute -top-16 -right-16 rtl:-left-16 rtl:right-auto" />
                  {heroStatCards.map((card) => (
                    <article
                      key={card.title}
                      className={`absolute w-[47%] h-[46%] rounded-2xl overflow-hidden border-[6px] border-white shadow-2xl transition-all duration-500 ease-out ${card.initial}`}
                    >
                      <img src={card.image} alt={card.title} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/25 to-transparent" />
                      <div className="absolute bottom-3 left-4 right-4">
                        <p className="text-white text-sm font-bold tracking-wide">{card.title}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
          <FadeIn mode="mount" delay={200} className="hidden lg:block relative">
            <div className="aspect-square bg-gradient-to-br from-secondary/20 to-transparent rounded-full blur-3xl absolute -top-20 -right-20 rtl:-left-20 rtl:right-auto" />
            <div className="group relative h-[520px]">
              {heroStatCards.map((card) => (
                <article
                  key={card.title}
                  className={`absolute w-[47%] h-[46%] rounded-2xl overflow-hidden border-[8px] border-white shadow-2xl transition-all duration-500 ease-out ${card.initial}`}
                >
                  <img src={card.image} alt={card.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/25 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white text-sm font-bold tracking-wide">{card.title}</p>
                  </div>
                </article>
              ))}
            </div>
          </FadeIn>
        </div>
      </header>

      {/* Partner Ad Banner - Top */}
      <section className="py-10 bg-surface-container-lowest">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-8">
          <FadeIn>
            <div className="mx-auto max-w-4xl space-y-4">
              <h3 className="text-2xl sm:text-3xl font-headline font-bold text-secondary text-center">
                {t('partnerAds.topBanner.videoTitle')}
              </h3>
              <div className="rounded-2xl overflow-hidden border border-secondary/30 bg-surface">
              <video
                className="w-full aspect-video bg-black object-contain"
                src={language === 'ar' ? nadyHololArabicVideo : hololClubEnglishVideo}
                controls
                preload="metadata"
              />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="scroll-mt-24 py-20 sm:py-32 bg-surface">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-8">
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-start">
              <div className="space-y-12">
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold text-secondary uppercase">
                    {t('about.kicker')}
                  </h2>
                  <h3 className="text-3xl sm:text-5xl font-headline font-bold text-primary tracking-tight">
                    {t('about.title')}
                  </h3>
                </div>
                <div className="space-y-6 font-bold text-on-surface-variant text-lg leading-relaxed">
                  <p>{t('about.p1')}</p>
                  <p>{t('about.p2')}</p>
                  <p>{t('about.p3')}</p>
                </div>
                <div className="grid grid-cols-2 gap-8 pt-8 border-t border-outline-variant/30">
                  <div>
                    <span className="block text-3xl font-headline font-bold text-primary">
                      {t('about.strategicTitle')}
                    </span>
                    <span className="mt-2 block text-on-surface-variant">{t('about.strategicDesc')}</span>
                  </div>
                  <div>
                    <span className="block text-3xl font-headline font-bold text-primary">
                      {t('about.globalTitle')}
                    </span>
                    <span className="mt-2 block text-on-surface-variant">{t('about.globalDesc')}</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-3xl p-8 sm:p-12 relative overflow-hidden group">
                  <img
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    alt={t('about.cardImgAlt')}
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1400&q=80"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/65 via-primary/35 to-primary/15" />
                  <div className="relative z-10 space-y-8">
                    <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center text-secondary">
                      <span className="material-symbols-outlined text-3xl" aria-hidden="true">
                        account_balance
                      </span>
                    </div>
                    <h4 className="text-3xl font-headline font-bold text-white">{t('about.cardTitle')}</h4>
                    <p className="text-white/90">{t('about.cardBody')}</p>
                  </div>
                </div>
                <div className="rounded-2xl bg-surface-container-low p-4 sm:p-5 border border-outline-variant/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <p className="text-on-surface-variant font-semibold">{t('about.learnMoreText')}</p>
                  <a
                    href="/about-us"
                    className="inline-flex items-center justify-center px-5 py-2.5 bg-secondary text-white rounded-lg font-bold hover:bg-tertiary transition-colors"
                  >
                    {t('about.learnMoreCta')}
                  </a>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services" className="scroll-mt-24 py-20 sm:py-32 bg-surface-container-lowest">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-8">
          <FadeIn>
            <div className="text-center max-w-3xl mx-auto mb-20 space-y-4">
              <h2 className="text-2xl font-bold text-secondary uppercase tracking-[0.3em]">
                {t('services.kicker')}
              </h2>
              <h3 className="text-4xl sm:text-4xl font-headline font-bold text-primary tracking-tight">
                {t('services.title')}
              </h3>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {messages.serviceItems.map((item, i) => (
              <FadeIn key={item.title} delay={i * 60}>
                <Link
                  to={`/services/${messages.serviceSlugs[i]}`}
                  className="group block p-10 border border-outline-variant/20 rounded-[2rem] transition-all duration-500 h-full relative overflow-hidden cursor-pointer"
                >
                  <img
                    src={serviceCardImages[i]}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/70 to-primary/45" />
                  <div className="relative z-10 flex justify-between items-start mb-12">
                    <div className="p-4 bg-secondary/90 rounded-2xl">
                      <span
                        className="material-symbols-outlined text-3xl text-white"
                        aria-hidden="true"
                      >
                        {serviceIcons[i]}
                      </span>
                    </div>
                    <span className="text-secondary font-bold text-xl">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="relative z-10 space-y-4">
                    <h4 className="text-2xl font-headline font-bold text-white">
                      {item.title}
                    </h4>
                    <h5 className="text-secondary font-medium">{item.subtitle}</h5>
                    <p className="text-white/85">{item.desc}</p>
                    <div className="flex items-center gap-1.5 text-secondary font-semibold text-sm pt-2">
                      <span>{language === 'ar' ? 'اعرف المزيد' : 'Learn more'}</span>
                      <span className={`material-symbols-outlined text-base ${language === 'ar' ? 'rotate-180' : ''}`}>
                        arrow_forward
                      </span>
                    </div>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Ad Cards */}
      <section id="idea" className="scroll-mt-24 py-20 bg-surface">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-8">
          <FadeIn>
            <div className="text-center max-w-4xl mx-auto mb-4 space-y-4">
              <h2 className="text-3xl font-bold text-secondary uppercase tracking-[0.3em]">
                {partnerAds.cardsTitle}
              </h2>
              <div dir="ltr" className="flex items-center justify-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => scrollPartnerCards('left')}
                  className="w-11 h-11 rounded-full border border-black bg-black text-white hover:bg-black/90 hover:border-black transition-colors"
                  aria-label={partnerAds.scrollLeft}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    chevron_left
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => scrollPartnerCards('right')}
                  className="w-11 h-11 rounded-full border border-black bg-black text-white hover:bg-black/90 hover:border-black transition-colors"
                  aria-label={partnerAds.scrollRight}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    chevron_right
                  </span>
                </button>
              </div>
            </div>
          </FadeIn>

          <div
            ref={partnerCardsRef}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            className="flex gap-7 overflow-x-auto overflow-y-hidden snap-x snap-mandatory pb-3 -mb-3 [scrollbar-width:thin]"
          >
            {displayedPdfPages.map(({ page, src }) => (
              <FadeIn
                key={page}
                delay={page * 30}
                className="flex-none snap-start w-[320px] md:w-[calc((100%_-_1.75rem)_/_2)]"
              >
                <article className="relative w-full bg-transparent rounded-2xl overflow-hidden">
                  <img
                    src={src}
                    alt={`PDF page ${page}`}
                    className="w-full h-auto block object-contain bg-transparent"
                    loading="lazy"
                  />
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Mid-page Banner + Power Pillars carousel */}
      <section id="partners-banner" className="scroll-mt-24 py-12 bg-secondary">
        <FadeIn>
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 space-y-10">
            <div
              className="pb-2"
              aria-roledescription="carousel"
              aria-label={t('partnersBanner.powerPillarsTitle')}
              onMouseEnter={() => setPartnerCarouselPaused(true)}
              onMouseLeave={() => setPartnerCarouselPaused(false)}
            >
              <div className="relative overflow-hidden w-full" dir="ltr">
                <div
                  className="flex w-[200%] transition-transform duration-700 ease-in-out motion-reduce:transition-none"
                  style={{ transform: `translateX(-${partnerCarouselSlide * 50}%)` }}
                >
                  <div className="w-1/2 shrink-0 px-0 sm:px-1" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                    <div className="space-y-8" aria-live="polite">
                      <div className="text-center max-w-3xl mx-auto space-y-2">
                        <h4 className="text-xl sm:text-2xl font-headline font-bold text-primary">{t('partnersBanner.powerPillarsTitle')}</h4>
                        <p className="partners-banner-subtitle text-primary/85 text-sm sm:text-base">
                          {t('partnersBanner.powerPillarsSubtitle')}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                        {powerPillars.map((pillar) => (
                          <div
                            key={pillar.title}
                            className="relative overflow-hidden rounded-2xl border border-white/25 shadow-md min-h-[210px] group"
                          >
                            <div
                              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-105"
                              style={
                                pillar.image ? { backgroundImage: `url(${pillar.image})` } : undefined
                              }
                              aria-hidden="true"
                            />
                            <div
                              className="absolute inset-0 bg-gradient-to-br from-primary/92 via-primary/78 to-primary/55"
                              aria-hidden="true"
                            />
                            <div className="relative z-10 p-5 sm:p-6 flex flex-col min-h-[210px]">
                              <span
                                className="material-symbols-outlined text-4xl sm:text-5xl text-secondary drop-shadow-sm mb-3"
                                aria-hidden="true"
                              >
                                {pillar.icon}
                              </span>
                              <p className="partners-card-headline font-bold text-white text-lg mb-2 drop-shadow">{pillar.title}</p>
                              <p className="partners-card-text text-white/90 text-sm leading-relaxed drop-shadow-sm">{pillar.body}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="w-1/2 shrink-0 px-0 sm:px-1" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                    <div className="space-y-8" aria-live="polite">
                      <div className="text-center max-w-3xl mx-auto space-y-2">
                        <h4 className="text-xl sm:text-2xl font-headline font-bold text-primary">{t('partnersBanner.successPartnersTitle')}</h4>
                        <p className="partners-banner-subtitle text-primary/85 text-sm sm:text-base">
                          {t('partnersBanner.successPartnersSubtitle')}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                        {successPartners.map((item) => (
                          <div
                            key={item.title}
                            className="relative overflow-hidden rounded-2xl border border-white/25 shadow-md min-h-[210px] group"
                          >
                            <div
                              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-105"
                              style={item.image ? { backgroundImage: `url(${item.image})` } : undefined}
                              aria-hidden="true"
                            />
                            <div
                              className="absolute inset-0 bg-gradient-to-br from-primary/92 via-primary/78 to-primary/55"
                              aria-hidden="true"
                            />
                            <div className="relative z-10 p-5 sm:p-6 flex flex-col min-h-[210px]">
                              <span
                                className="material-symbols-outlined text-4xl sm:text-5xl text-secondary drop-shadow-sm mb-3"
                                aria-hidden="true"
                              >
                                {item.icon}
                              </span>
                              <p className="partners-card-headline font-bold text-white text-lg mb-2 drop-shadow">{item.title}</p>
                              <p className="partners-card-text text-white/90 text-sm leading-relaxed drop-shadow-sm">{item.body}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center gap-2 mt-8" role="tablist" aria-label="Partner carousel">
                {[0, 1].map((i) => (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={partnerCarouselSlide === i}
                    aria-label={i === 0 ? t('partnersBanner.powerPillarsTitle') : t('partnersBanner.successPartnersTitle')}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      partnerCarouselSlide === i ? 'w-9 bg-primary' : 'w-2.5 bg-primary/35 hover:bg-primary/50'
                    }`}
                    onClick={() => setPartnerCarouselSlide(i)}
                  />
                ))}
              </div>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-center gap-8 pt-8 border-t border-primary/25">
              <h3 className="text-2xl font-headline font-bold text-primary text-center md:text-start">
                {t('partnersBanner.title')}
              </h3>
              <button
                type="button"
                onClick={scrollToRegistration}
                className="bg-primary text-white px-10 py-4 rounded-xl font-bold hover:bg-primary-container transition-all shadow-xl shrink-0"
              >
                {t('partnersBanner.cta')}
              </button>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* Agent Benefits Zigzag */}
      <section id="joining-benefits" className="scroll-mt-24 py-24 bg-surface">
        <div className="max-w-screen-2xl mx-auto px-8">
          <FadeIn>
            <div className="text-center max-w-4xl mx-auto mb-14 space-y-4">
              <h2 className="text-2xl font-bold text-secondary uppercase tracking-[0.3em]">
                {agentBenefits.kicker}
              </h2>
              <h3 className="text-4xl sm:text-5xl font-headline font-bold text-primary tracking-tight">
                {agentBenefits.title}
              </h3>
            </div>
          </FadeIn>

          <div className="space-y-8">
            {agentBenefits.items.map((item, i) => (
              <FadeIn key={item.title} delay={i * 80}>
                <article className="rounded-[2rem] border border-outline-variant/30 bg-surface-container-low overflow-hidden">
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className={`${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                      <img src={item.image} alt={item.title} className="w-full h-full min-h-[260px] object-cover" />
                    </div>
                    <div className={`p-8 sm:p-10 ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <h4
                        className={`text-2xl font-headline font-bold text-secondary mb-4 ${
                          item.sections ? 'text-center' : ''
                        }`}
                      >
                        {item.title}
                      </h4>
                      {item.sections ? (
                        <div className="space-y-6">
                          {item.intro ? (
                            <p className="text-on-surface-variant leading-relaxed font-medium text-center max-w-3xl mx-auto">
                              {item.intro}
                            </p>
                          ) : null}
                          {item.sections.map((sec) => (
                            <div
                              key={sec.heading}
                              className="rounded-2xl border border-outline-variant/30 bg-surface/80 p-5 sm:p-6 shadow-sm"
                            >
                              <h5 className="text-primary font-extrabold text-lg leading-snug">{sec.heading}</h5>
                              <div className="mt-1.5 mb-4 flex gap-2.5 items-start">
                                <span
                                  className="mt-2 h-2 w-2 shrink-0 rounded-full bg-secondary shadow-[0_0_0_3px_rgba(119,90,25,0.2)]"
                                  aria-hidden="true"
                                />
                                <p className="flex-1 text-sm sm:text-base text-on-surface-variant leading-relaxed">
                                  {sec.subtitle}
                                </p>
                              </div>
                              <ul className="space-y-3 text-on-surface-variant leading-relaxed">
                                {sec.points.map((point) => (
                                  <li key={point} className="flex gap-3 items-start">
                                    <span className="mt-1 w-2 h-2 rounded-full bg-secondary shrink-0" />
                                    <span>{point}</span>
                                  </li>
                                ))}
                              </ul>
                              <div className="mt-6 pt-4 border-t border-outline-variant/30 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                <p className="text-sm leading-relaxed text-secondary font-semibold">{sec.footerText}</p>
                                <button
                                  type="button"
                                  onClick={() => scrollToSectionById(sec.footerHash)}
                                  className="shrink-0 inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-secondary text-white font-bold text-sm hover:bg-tertiary transition-colors"
                                >
                                  {sec.footerCta}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <ul className="space-y-3 text-on-surface-variant leading-relaxed">
                          {item.body.map((point) =>
                            isBenefitSubheading(point) ? (
                              <li key={point} className="pt-2">
                                <span className="text-primary font-extrabold text-lg">{point}</span>
                              </li>
                            ) : (
                              <li key={point} className="flex gap-3 items-start">
                                <span className="mt-1 w-2 h-2 rounded-full bg-secondary shrink-0" />
                                <span>{point}</span>
                              </li>
                            )
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                </article>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Application Forms */}
      <section id="registration" className="scroll-mt-24 py-20 sm:py-32 bg-surface-container-low">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <FadeIn>
            <div className="relative overflow-hidden rounded-[2rem] mb-16 border border-outline-variant/25 shadow-lg">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('https://deltatechco.com/picupload/cms/l201607011467340927.jpg')",
                }}
                aria-hidden
              />
              <div className="absolute inset-0 bg-gradient-to-b from-surface-container-low/92 via-surface-container-low/88 to-surface-container-low/95 dark:from-primary/90 dark:via-primary/85 dark:to-primary/92" />
              <div className="relative z-10 flex flex-col items-center text-center py-12 sm:py-16 px-6 sm:px-10 gap-5 sm:gap-6">
                <button
                  type="button"
                  onClick={() => scrollToSectionById('registration-form')}
                  className="inline-flex items-center justify-center rounded-full border-2 border-white/95 bg-white/15 px-10 py-3.5 text-base sm:text-lg font-bold uppercase tracking-[0.28em] text-white shadow-[0_8px_32px_rgba(0,0,0,0.35)] backdrop-blur-[2px] transition hover:bg-white/25 hover:scale-[1.02] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                  style={{
                    textShadow:
                      '0 0 1px rgba(255,255,255,0.95), 0 0 12px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.55)',
                  }}
                >
                  {t('registration.kicker')}
                </button>
                <h3
                  className="text-2xl sm:text-3xl font-headline font-bold text-white tracking-tight max-w-3xl mx-auto leading-snug"
                  style={{
                    textShadow:
                      '0 0 1px rgba(255,255,255,0.85), 0 0 10px rgba(0,0,0,0.4), 0 2px 5px rgba(0,0,0,0.5)',
                  }}
                >
                  {t('registration.title')}
                </h3>
              </div>
            </div>
          </FadeIn>
          <FadeIn delay={80}>
            <div
              id="registration-form"
              className="bg-surface-container-lowest rounded-[2.5rem] shadow-2xl overflow-hidden scroll-mt-24"
            >
              <div className="flex border-b border-outline-variant/30 bg-primary">
                <button
                  type="button"
                  onClick={() => setRegTab('partner')}
                  className={`flex-1 py-8 text-lg font-bold border-b-4 transition-colors first:rounded-tl-[2.5rem] ${
                    regTab === 'partner'
                      ? 'bg-secondary text-white border-secondary'
                      : 'bg-primary text-white border-transparent hover:bg-primary/85'
                  }`}
                >
                  {t('registration.tabPartner')}
                </button>
                <button
                  type="button"
                  onClick={() => setRegTab('provider')}
                  className={`flex-1 py-8 text-lg font-bold border-b-4 transition-colors last:rounded-tr-[2.5rem] ${
                    regTab === 'provider'
                      ? 'bg-secondary text-white border-secondary'
                      : 'bg-primary text-white border-transparent hover:bg-primary/85'
                  }`}
                >
                  {t('registration.tabProvider')}
                </button>
              </div>
              <form className="p-6 sm:p-12 space-y-8" onSubmit={handleApplicationSubmit}>
                {regTab === 'partner' ? (
                  language === 'ar' ? (
                  <div dir="rtl" className="space-y-8 text-start">
                    <div className={regFormStep === 0 ? 'space-y-0' : 'hidden'}>
                      <div className="rounded-2xl bg-surface-container-low p-6 border border-outline-variant/30 space-y-3">
                        <h4 className="text-2xl font-headline font-bold text-primary">نموذج طلب انضمام شريك محلي (وكيل دولة)</h4>
                        <p className="text-on-surface-variant leading-relaxed">
                          عزيزي الشريك المحتمل، نحن في منصة نادي حلول نسعى لبناء أكبر منظومة متكاملة لخدمات
                          الأعمال في المنطقة. إذا كانت شركتكم تمتلك الخبرة و القدرة التشغيلية لإدارة سوق
                          كامل، يسعدنا التقديم عبر هذا النموذج.
                        </p>
                      </div>
                    </div>

                    <div className={regFormStep === 1 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">أولاً: معلومات الكيان القانوني</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">اسم الشركة/المؤسسة</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="legalCompanyName" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">الدولة المستهدفة بالوكالة</label>
                          <select className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" name="targetCountry" defaultValue="مصر">
                            <option>مصر</option>
                            <option>السعودية</option>
                            <option>الإمارات</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">تاريخ التأسيس</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="date" name="establishedDate" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">عدد الموظفين الحاليين</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="number" name="employeeCount" min="1" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">رقم السجل التجاري</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="commercialRegistry" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">إرفاق نسخة من السجل التجاري</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="file" name="commercialRegistryFile" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">الموقع الإلكتروني ورابط LinkedIn للشركة</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={3} name="webAndLinkedIn" />
                      </div>
                    </div>

                    <div className={regFormStep === 2 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">ثانياً: الخبرات والقدرات (التقييم الفني)</h5>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">ما هو مجال التخصص الرئيسي لشركتكم حالياً؟</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-on-surface">
                          <label className="flex items-center gap-2"><input type="radio" name="specialization" value="legal" />استشارات قانونية</label>
                          <label className="flex items-center gap-2"><input type="radio" name="specialization" value="accounting" />محاسبة ومراجعة</label>
                          <label className="flex items-center gap-2"><input type="radio" name="specialization" value="financing" />تمويل</label>
                          <label className="flex items-center gap-2"><input type="radio" name="specialization" value="other" />أخرى</label>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">هل تمتلك الشركة حالياً شبكة من شركاء تقديم الخدمة؟</label>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2"><input type="radio" name="hasNetwork" value="yes" />نعم</label>
                          <label className="flex items-center gap-2"><input type="radio" name="hasNetwork" value="no" />لا</label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">إذا كانت الإجابة نعم، يرجى ذكر عدد الشركات الشريكة في مجالات (الاستشارات القانونية، المحاسبية، التمويل، التسويق، دراسات الجدوى، الاقامة و الهجرة)</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={3} name="partnersCountByDomain" />
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">هل لديكم شبكة علاقات جيدة بالبنوك و النوادي الرياضية في دولتكم</label>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2"><input type="radio" name="hasBankAndClubsNetwork" value="yes" />نعم</label>
                          <label className="flex items-center gap-2"><input type="radio" name="hasBankAndClubsNetwork" value="no" />لا</label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">اذكر عدد منها علي سبيل المثال</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="bankAndClubsExamplesCount" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">اذكر أهم 3 إنجازات أو عملاء (شركات) قامت شركتكم بخدمتهم في آخر عامين</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={4} name="topAchievements" />
                      </div>
                    </div>

                    <div className={regFormStep === 3 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">ثالثاً: الرؤية التشغيلية</h5>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">كيف تخطط لضمان جودة الاستشارات المقدمة عبر التطبيق في دولتكم؟</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={4} name="qualityPlan" />
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">هل تتوفر لديكم البنية التحتية لإدارة دعم العملاء (Call Center/Help Desk)؟</label>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2"><input type="radio" name="supportInfra" value="yes" />نعم</label>
                          <label className="flex items-center gap-2"><input type="radio" name="supportInfra" value="no" />لا</label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">ما هو الوقت المتوقع لرد فريقكم على طلبات العملاء العاجلة؟ (بالساعات)</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="number" min="1" name="urgentResponseHours" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">هل تقدمون الخدمة بأكثر من لغة؟</label>
                        <div className="flex flex-wrap items-center gap-6">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="العربية" />
                            العربية
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="الإنجليزية" />
                            الإنجليزية
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="الفرنسية" />
                            الفرنسية
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="أخرى" />
                            أخرى
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className={regFormStep === 4 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">رابعاً: الملاءة المالية والالتزام</h5>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">هل لديكم القدرة على تخصيص ميزانية تسويقية محلية داخل دولتكم للترويج للتطبيق؟</label>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2"><input type="radio" name="marketingBudget" value="yes" />نعم، ميزانية محددة سنوياً</label>
                          <label className="flex items-center gap-2"><input type="radio" name="marketingBudget" value="central" />نعتمد على التسويق المركزي للمنصة الأم</label>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">هل هناك أي موانع قانونية أو تعاقدية تمنعكم من تمثيل منصتنا حصرياً؟</label>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2"><input type="radio" name="exclusiveRestriction" value="yes" />نعم</label>
                          <label className="flex items-center gap-2"><input type="radio" name="exclusiveRestriction" value="no" />لا</label>
                        </div>
                      </div>
                    </div>

                    <div className={regFormStep === 5 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">خامساً: بيانات التواصل</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">اسم الشخص المسؤول عن التنسيق</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="contactCoordinatorName" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">رقم الجوال (واتساب)</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="tel" name="contactWhatsapp" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">البريد الإلكتروني الرسمي</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="email" name="contactOfficialEmail" />
                      </div>
                    </div>

                    <div className={regFormStep === 6 ? 'space-y-5' : 'hidden'}>
                      <div className="rounded-2xl bg-surface-container-low p-6 border border-outline-variant/30 space-y-4">
                        <h5 className="text-lg font-bold text-primary">إقرار</h5>
                        <p className="text-on-surface-variant">
                          أقر أنا الموقع أدناه بصحة كافة البيانات الواردة في هذا الطلب، وأوافق على تزويد إدارة
                          التطبيق بأي مستندات إضافية عند الطلب.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-on-surface-variant">التوقيع / الختم</label>
                            <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="declarationSignature" />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-on-surface-variant">التاريخ (2026)</label>
                            <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="date" name="declarationDate" />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl bg-surface-container-low p-6 border border-outline-variant/30 space-y-4">
                        <h5 className="text-lg font-bold text-primary">المستندات المطلوبة للإرفاق</h5>
                        <ul className="space-y-2 text-on-surface-variant">
                          <li>بروفايل الشركة (Company Profile) المحدث.</li>
                          <li>قائمة بأسماء فريق الإدارة المقترح لهذا المشروع.</li>
                        </ul>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="file" name="requiredDocs" multiple />
                      </div>
                    </div>
                  </div>
                  ) : (
                  <div dir="ltr" className="space-y-8 text-start">
                    <div className={regFormStep === 0 ? 'space-y-0' : 'hidden'}>
                      <div className="rounded-2xl bg-surface-container-low p-6 border border-outline-variant/30 space-y-3">
                        <h4 className="text-2xl font-headline font-bold text-primary">Local Partner (Country Agent) Application Form</h4>
                        <p className="text-on-surface-variant leading-relaxed">
                          Dear potential partner, at holol Club we are building the largest integrated business
                          services ecosystem in the region. If your company has the expertise, passion, and
                          operational capability to manage a full market, please apply through this form.
                        </p>
                      </div>
                    </div>

                    <div className={regFormStep === 1 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">1) Legal Entity Information</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">Company / Establishment Name</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="legalCompanyName" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">Target Agency Country</label>
                          <select className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" name="targetCountry" defaultValue="Egypt">
                            <option>Egypt</option>
                            <option>Saudi Arabia</option>
                            <option>UAE</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">Date of Establishment</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="date" name="establishedDate" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">Current Number of Employees</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="number" name="employeeCount" min="1" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">Commercial Registration Number</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="commercialRegistry" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">Attach Commercial Registration Copy</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="file" name="commercialRegistryFile" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Company Website and LinkedIn URL</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={3} name="webAndLinkedIn" />
                      </div>
                    </div>

                    <div className={regFormStep === 2 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">2) Experience & Capabilities (Technical Evaluation)</h5>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">What is your company’s main current specialization?</label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-on-surface">
                          <label className="flex items-center gap-2"><input type="radio" name="specialization" value="legal" />Legal Consulting</label>
                          <label className="flex items-center gap-2"><input type="radio" name="specialization" value="accounting" />Accounting & Audit</label>
                          <label className="flex items-center gap-2"><input type="radio" name="specialization" value="management" />Management & Business Development Consulting</label>
                          <label className="flex items-center gap-2"><input type="radio" name="specialization" value="other" />Other</label>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">Does your company currently have a service-provider partner network?</label>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2"><input type="radio" name="hasNetwork" value="yes" />Yes</label>
                          <label className="flex items-center gap-2"><input type="radio" name="hasNetwork" value="no" />No</label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">If yes, how many partner companies do you have in financing, marketing, and feasibility studies?</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={3} name="partnersCountByDomain" />
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">Do you have strong relationship networks with banks and sports clubs in your country?</label>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2"><input type="radio" name="hasBankAndClubsNetwork" value="yes" />Yes</label>
                          <label className="flex items-center gap-2"><input type="radio" name="hasBankAndClubsNetwork" value="no" />No</label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Please provide sample counts (for example)</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="bankAndClubsExamplesCount" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">List your top 3 achievements or client companies served in the last two years</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={4} name="topAchievements" />
                      </div>
                    </div>

                    <div className={regFormStep === 3 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">3) Operational Vision</h5>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">How do you plan to ensure consulting quality through the app in your country?</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={4} name="qualityPlan" />
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">Do you have infrastructure for customer support management (Call Center / Help Desk)?</label>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2"><input type="radio" name="supportInfra" value="yes" />Yes</label>
                          <label className="flex items-center gap-2"><input type="radio" name="supportInfra" value="no" />No</label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Expected response time for urgent client requests (hours)</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="number" min="1" name="urgentResponseHours" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Do you provide services in more than one language?</label>
                        <div className="flex flex-wrap items-center gap-6">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="Arabic" />
                            Arabic
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="English" />
                            English
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="French" />
                            French
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="Other" />
                            Other
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className={regFormStep === 4 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">4) Financial Capacity & Commitment</h5>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">Can you allocate a local annual marketing budget to promote the app?</label>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2"><input type="radio" name="marketingBudget" value="yes" />Yes, with a defined annual budget</label>
                          <label className="flex items-center gap-2"><input type="radio" name="marketingBudget" value="central" />We rely on central platform marketing</label>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">Are there legal or contractual restrictions preventing exclusive representation of our platform?</label>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2"><input type="radio" name="exclusiveRestriction" value="yes" />Yes</label>
                          <label className="flex items-center gap-2"><input type="radio" name="exclusiveRestriction" value="no" />No</label>
                        </div>
                      </div>
                    </div>

                    <div className={regFormStep === 5 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">5) Contact Details</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">Coordinator Full Name</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="contactCoordinatorName" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">Mobile Number (WhatsApp)</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="tel" name="contactWhatsapp" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Official Email</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="email" name="contactOfficialEmail" />
                      </div>
                    </div>

                    <div className={regFormStep === 6 ? 'space-y-5' : 'hidden'}>
                      <div className="rounded-2xl bg-surface-container-low p-6 border border-outline-variant/30 space-y-4">
                        <h5 className="text-lg font-bold text-primary">Declaration</h5>
                        <p className="text-on-surface-variant">
                          I, the undersigned, hereby confirm that all information provided in this application is accurate, and I agree to provide additional documents requested by app management when needed.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-on-surface-variant">Signature / Stamp</label>
                            <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="declarationSignature" />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-on-surface-variant">Date (2026)</label>
                            <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="date" name="declarationDate" />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl bg-surface-container-low p-6 border border-outline-variant/30 space-y-4">
                        <h5 className="text-lg font-bold text-primary">Required Attachments</h5>
                        <ul className="space-y-2 text-on-surface-variant">
                          <li>Updated company profile.</li>
                          <li>List of proposed management team members for this project.</li>
                        </ul>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="file" name="requiredDocs" multiple />
                      </div>
                    </div>
                  </div>
                  )
                ) : language === 'en' ? (
                  <div dir="ltr" className="space-y-8 text-start">
                    <div className={regFormStep === 0 ? 'space-y-0' : 'hidden'}>
                      <div className="rounded-2xl bg-surface-container-low p-6 border border-outline-variant/30 space-y-3">
                        <h4 className="text-2xl font-headline font-bold text-primary">Service Provider Application Form</h4>
                        <p className="text-on-surface-variant leading-relaxed">
                          We are pleased by your interest in joining our platform as a success partner to deliver exceptional
                          services to our users. Please complete the following information accurately so we can evaluate your
                          application and start the cooperation process.
                        </p>
                      </div>
                    </div>

                    <div className={regFormStep === 1 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">1) General Information</h5>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Company/Organization Name (or Full Name for Individuals)</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="providerName" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Head Office (Full Address)</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={3} name="providerHeadquarters" />
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">Countries currently covered by your services</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label className="flex items-center gap-2"><input type="checkbox" name="coverageCountries" value="Egypt" />Egypt</label>
                          <label className="flex items-center gap-2"><input type="checkbox" name="coverageCountries" value="Saudi Arabia" />Saudi Arabia</label>
                          <label className="flex items-center gap-2"><input type="checkbox" name="coverageCountries" value="UAE" />UAE</label>
                          <label className="flex items-center gap-2"><input type="checkbox" name="coverageCountries" value="Other" />Other</label>
                        </div>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="coverageOther" placeholder="If other, please specify" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">Commercial Registration Number</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="providerCommercialRegistry" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">Website / Social Media Links</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="providerLinks" />
                        </div>
                      </div>
                    </div>

                    <div className={regFormStep === 2 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">2) Service Details</h5>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">What services would you like to offer through the app (including pricing)?</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={3} name="serviceTypes" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">What competitive advantages does your company offer to attract more customers through the app?</label>
                        <div className="flex flex-wrap items-center gap-6">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="competitiveFeatures" value="Discounts" />
                            Discounts
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="competitiveFeatures" value="Free Services" />
                            Free Services
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="competitiveFeatures" value="Other" />
                            Other
                          </label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Please provide details of these offerings</label>
                        <textarea
                          className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary"
                          rows={4}
                          name="competitiveFeaturesDetails"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Do you provide services in more than one language?</label>
                        <div className="flex flex-wrap items-center gap-6">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="Arabic" />
                            Arabic
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="English" />
                            English
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="French" />
                            French
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="Other" />
                            Other
                          </label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Which cooperation model do you prefer with holol Club?</label>
                        <div className="flex flex-wrap items-center gap-6">
                          <label className="flex items-center gap-2">
                            <input type="radio" name="cooperationMethod" value="Annual Subscription Model" />
                            Annual Subscription Model
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="radio" name="cooperationMethod" value="Commission Model" />
                            Commission Model
                          </label>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">Do you have the required licenses to practice this profession in the specified countries?</label>
                        <div className="flex flex-wrap items-center gap-6">
                          <label className="flex items-center gap-2"><input type="radio" name="hasLicenses" value="yes" />Yes</label>
                          <label className="flex items-center gap-2"><input type="radio" name="hasLicenses" value="no" />No</label>
                          <label className="flex items-center gap-2"><input type="radio" name="hasLicenses" value="pending" />In Progress</label>
                        </div>
                      </div>
                    </div>

                    <div className={regFormStep === 3 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">3) Operational and Technical Capability</h5>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Size of the team dedicated to fulfilling requests</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="operationsTeamSize" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Detailed geographic coverage (cities and areas you can serve)</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={3} name="coverageDetails" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Do you have previous work or an existing client list? (Please mention the top 3 examples)</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={4} name="topClientExamples" placeholder="1-&#10;2-&#10;3-" />
                      </div>
                    </div>

                    <div className={regFormStep === 4 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">4) Quality and Cooperation Standards</h5>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Average response and delivery time for requests</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="avgResponseTime" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Do you provide a warranty for your services? If yes, what is the duration?</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={3} name="serviceWarranty" />
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">Are you willing to comply with the commission/fees model agreed upon with app management?</label>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2"><input type="radio" name="acceptCommissionSystem" value="yes" />Yes</label>
                          <label className="flex items-center gap-2"><input type="radio" name="acceptCommissionSystem" value="no" />No</label>
                        </div>
                      </div>
                    </div>

                    <div className={regFormStep === 5 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">5) Contact Details</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">Coordinator Full Name</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="contactCoordinatorName" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">Mobile Number (WhatsApp)</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="tel" name="contactWhatsapp" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">Official Email</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="email" name="contactOfficialEmail" />
                      </div>
                    </div>

                    <div className={regFormStep === 6 ? 'space-y-5' : 'hidden'}>
                      <div className="rounded-2xl bg-surface-container-low p-6 border border-outline-variant/30 space-y-4">
                        <h5 className="text-lg font-bold text-primary">Declaration</h5>
                        <p className="text-on-surface-variant">
                          I, the undersigned, hereby confirm that all information provided in this application is accurate, and I agree to provide additional documents requested by app management when needed.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-on-surface-variant">Signature / Stamp</label>
                            <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="declarationSignature" />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-on-surface-variant">Date (2026)</label>
                            <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="date" name="declarationDate" />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl bg-surface-container-low p-6 border border-outline-variant/30 space-y-4">
                        <h5 className="text-lg font-bold text-primary">Required Attachments</h5>
                        <ul className="space-y-2 text-on-surface-variant">
                          <li>Updated company profile.</li>
                          <li>List of names of the proposed management team for this project.</li>
                        </ul>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="file" name="providerRequiredDocs" multiple />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div dir="rtl" className="space-y-8 text-start">
                    <div className={regFormStep === 0 ? 'space-y-0' : 'hidden'}>
                      <div className="rounded-2xl bg-surface-container-low p-6 border border-outline-variant/30 space-y-3">
                        <h4 className="text-2xl font-headline font-bold text-primary">نموذج طلب انضمام مقدم خدمة (Service Provider)</h4>
                        <p className="text-on-surface-variant leading-relaxed">
                          يسعدنا اهتمامكم بالانضمام إلى منصتنا كشركاء نجاح لتقديم خدمات متميزة لمستخدمينا. يرجى
                          ملء البيانات التالية بدقة لتقييم طلبكم وبدء إجراءات التعاون.
                        </p>
                      </div>
                    </div>

                    <div className={regFormStep === 1 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">أولاً: المعلومات العامة</h5>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">اسم الشركة / المؤسسة (أو الاسم الثلاثي للأفراد)</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="providerName" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">المقر الرئيسي (العنوان بالتفصيل)</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={3} name="providerHeadquarters" />
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">الدول التي تغطيها خدماتكم حالياً</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <label className="flex items-center gap-2"><input type="checkbox" name="coverageCountries" value="مصر" />مصر</label>
                          <label className="flex items-center gap-2"><input type="checkbox" name="coverageCountries" value="السعودية" />السعودية</label>
                          <label className="flex items-center gap-2"><input type="checkbox" name="coverageCountries" value="الإمارات" />الإمارات</label>
                          <label className="flex items-center gap-2"><input type="checkbox" name="coverageCountries" value="أخرى" />أخرى</label>
                        </div>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="coverageOther" placeholder="في حال أخرى، يرجى الذكر" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">رقم السجل التجاري</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="providerCommercialRegistry" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">الموقع الإلكتروني / روابط منصات التواصل الاجتماعي</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="providerLinks" />
                        </div>
                      </div>
                    </div>

                    <div className={regFormStep === 2 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">ثانياً: تفاصيل الخدمة المقدمة</h5>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant"> نوع الخدمات التي ترغب بتقديمها عبر التطبيق شامل سعر الخدمة</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={3} name="serviceTypes" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">ما هي المميزات التنافسية التي تقدمها شركتكم لجزب اكبر عدد من العملاء من خلال التطبيق</label>
                        <div className="flex flex-wrap items-center gap-6">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="competitiveFeatures" value="خصومات" />
                            خصومات
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="competitiveFeatures" value="خدمات مجانية" />
                            خدمات مجانية
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="competitiveFeatures" value="أخرى" />
                            أخرى
                          </label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">اذكر هذه الخدمات بالتفصيل</label>
                        <textarea
                          className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary"
                          rows={4}
                          name="competitiveFeaturesDetails"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">هل تقدمون الخدمة بأكثر من لغة؟</label>
                        <div className="flex flex-wrap items-center gap-6">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="العربية" />
                            العربية
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="الإنجليزية" />
                            الإنجليزية
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="الفرنسية" />
                            الفرنسية
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" name="serviceLanguages" value="أخرى" />
                            أخرى
                          </label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">ما هي الطريقة التي تفضلها للتعاون معنا في نادي حلول</label>
                        <div className="flex flex-wrap items-center gap-6">
                          <label className="flex items-center gap-2">
                            <input type="radio" name="cooperationMethod" value="نظام الاشتراك السنوي" />
                            نظام الاشتراك السنوي
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="radio" name="cooperationMethod" value="نظام العمولة" />
                            نظام العمولة
                          </label>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">هل تتوفر لديكم التراخيص اللازمة لمزاولة هذه المهنة في الدول المحددة؟</label>
                        <div className="flex flex-wrap items-center gap-6">
                          <label className="flex items-center gap-2"><input type="radio" name="hasLicenses" value="yes" />نعم</label>
                          <label className="flex items-center gap-2"><input type="radio" name="hasLicenses" value="no" />لا</label>
                          <label className="flex items-center gap-2"><input type="radio" name="hasLicenses" value="pending" />قيد الاستخراج</label>
                        </div>
                      </div>
                    </div>

                    <div className={regFormStep === 3 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">ثالثاً: القدرات التشغيلية والتقنية</h5>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">حجم فريق العمل المخصص لتنفيذ الطلبات</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="operationsTeamSize" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">النطاق الجغرافي التفصيلي (المدن والمناطق التي يمكنك تغطيتها)</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={3} name="coverageDetails" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">هل لديكم سابقة أعمال أو قائمة عملاء حاليين؟ (يرجى ذكر أبرز 3 أمثلة)</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={4} name="topClientExamples" placeholder="1-&#10;2-&#10;3-" />
                      </div>
                    </div>

                    <div className={regFormStep === 4 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">رابعاً: معايير الجودة والتعاون</h5>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">متوسط الزمن المستغرق للاستجابة للطلب وتنفيذه</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="avgResponseTime" />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">هل تلتزم بتقديم ضمان على الخدمات المقدمة؟ وما هي مدته؟</label>
                        <textarea className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" rows={3} name="serviceWarranty" />
                      </div>
                      <div className="space-y-3">
                        <label className="text-sm font-semibold text-on-surface-variant">هل لديكم استعداد للالتزام بنظام العمولات أو الرسوم المتفق عليه مع إدارة التطبيق؟</label>
                        <div className="flex items-center gap-6">
                          <label className="flex items-center gap-2"><input type="radio" name="acceptCommissionSystem" value="yes" />نعم</label>
                          <label className="flex items-center gap-2"><input type="radio" name="acceptCommissionSystem" value="no" />لا</label>
                        </div>
                      </div>
                    </div>

                    <div className={regFormStep === 5 ? 'space-y-5' : 'hidden'}>
                      <h5 className="text-lg font-bold text-primary">خامساً: بيانات التواصل</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">اسم الشخص المسؤول عن التنسيق</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="contactCoordinatorName" />
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-on-surface-variant">رقم الجوال (واتساب)</label>
                          <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="tel" name="contactWhatsapp" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-on-surface-variant">البريد الإلكتروني الرسمي</label>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="email" name="contactOfficialEmail" />
                      </div>
                    </div>

                    <div className={regFormStep === 6 ? 'space-y-5' : 'hidden'}>
                      <div className="rounded-2xl bg-surface-container-low p-6 border border-outline-variant/30 space-y-4">
                        <h5 className="text-lg font-bold text-primary">إقرار</h5>
                        <p className="text-on-surface-variant">
                          أقر أنا الموقع أدناه بصحة كافة البيانات الواردة في هذا الطلب، وأوافق على تزويد إدارة
                          التطبيق بأي مستندات إضافية عند الطلب.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-on-surface-variant">التوقيع / الختم</label>
                            <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="text" name="declarationSignature" />
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-on-surface-variant">التاريخ (2026)</label>
                            <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="date" name="declarationDate" />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl bg-surface-container-low p-6 border border-outline-variant/30 space-y-4">
                        <h5 className="text-lg font-bold text-primary">المستندات المطلوبة للإرفاق</h5>
                        <ul className="space-y-2 text-on-surface-variant">
                          <li>بروفايل الشركة (Company Profile) المحدث.</li>
                          <li>قائمة بأسماء فريق الإدارة المقترح لهذا المشروع.</li>
                        </ul>
                        <input className="w-full px-5 py-3 rounded-xl bg-surface-container-high border-none focus:ring-2 focus:ring-secondary" type="file" name="providerRequiredDocs" multiple />
                      </div>
                    </div>
                  </div>
                )}
                <div
                  className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-outline-variant/20${
                    language === 'en' ? ' sm:flex-row-reverse' : ''
                  }`}
                  dir={language === 'ar' ? 'rtl' : 'ltr'}
                >
                  {regFormStep < registrationLastStep ? (
                    <button
                      type="button"
                      onClick={() => setRegFormStep((s) => Math.min(registrationLastStep, s + 1))}
                      className="px-6 py-3 rounded-xl bg-secondary text-white font-bold hover:opacity-95 transition-opacity"
                    >
                      {t('registration.formNext')}
                    </button>
                  ) : (
                    <span className="w-full sm:w-32 shrink-0" aria-hidden="true" />
                  )}
                  <span className="text-sm font-medium text-on-surface-variant text-center sm:flex-1" aria-live="polite">
                    {registrationStepLabel}
                  </span>
                  <button
                    type="button"
                    onClick={() => setRegFormStep((s) => Math.max(0, s - 1))}
                    disabled={regFormStep === 0}
                    className="px-6 py-3 rounded-xl border border-outline-variant/40 font-bold text-primary bg-surface-container-high hover:bg-surface-container transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {t('registration.formPrevious')}
                  </button>
                </div>
                {regFormStep === registrationLastStep && (
                  <>
                    {submitState.status !== 'idle' && (
                      <p
                        className={`text-sm font-semibold ${
                          submitState.status === 'success' ? 'text-green-600' : submitState.status === 'error' ? 'text-red-600' : 'text-primary'
                        }`}
                        role="status"
                      >
                        {submitState.status === 'submitting' ? t('registration.submitting') : submitState.message}
                      </p>
                    )}
                    <button
                      className="w-full py-5 bg-primary text-white rounded-xl font-bold text-xl hover:bg-primary-container transition-all flex items-center justify-center gap-3"
                      type="submit"
                      disabled={submitState.status === 'submitting'}
                    >
                      <span>{submitState.status === 'submitting' ? t('registration.submitting') : t('registration.submit')}</span>
                      <span className="material-symbols-outlined rtl:rotate-180" aria-hidden="true">
                        arrow_forward
                      </span>
                    </button>
                  </>
                )}
              </form>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Partner Ad Banner - Mid */}
      <section className="py-10 bg-surface-container-lowest">
        <div className="max-w-screen-2xl mx-auto px-8">
          <FadeIn>
            <div className="mx-auto w-fit max-w-full rounded-2xl border border-outline-variant/40 bg-primary text-white p-5 sm:p-7">
              <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5 items-center">
                <img
                  src={partnerAds.midBanner.image}
                  alt={partnerAds.midBanner.company}
                  className="w-full h-40 lg:h-32 object-cover rounded-xl"
                />
                <div className="space-y-2 text-center lg:text-start">
                  <p className="text-2xl; font-bold uppercase tracking-[0.25em] text-secondary">
                    {partnerAds.midBanner.label}
                  </p>
                  <h3 className="text-2xl text-center font-headline font-bold">{partnerAds.midBanner.company}</h3>
                  <p className="text-white/75 font-bold">{partnerAds.midBanner.details}</p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Bottom Banner */}
      <section id="contact" className="scroll-mt-24 py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-primary">
          <div className="absolute inset-0 bg-gradient-to-r from-secondary/20 to-transparent rtl:bg-gradient-to-l" />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "url('https://lh3.googleusercontent.com/aida-public/AB6AXuBjvvVxpnxIH6NVJaAYXtFwMIcEcRcpoUyA47xIUzEjBQWyTZml4kwzDBj0V9fNZjF1FsgAJnpsM0nsqLWIKYIVmEAb1MPZaQXPkfzTZonBz9BxpUFs-9ByTIXr5_N_iOigtIoWM3Nobp_ro_EgCC8MtkRT9QcJeUpWpjJKIjhMYj3TIQ8CVhkud7lEZwaQLdIk_daLJw9uj1VSNJdOhgpz0t_79CWK1vldsIvO4yoWJfJtcIE5ffEC441t020Mfh3TuNYrBqhMxm8')",
              opacity: 0.1,
            }}
          />
        </div>
        <FadeIn>
          <div className="relative z-10 max-w-screen-2xl mx-auto px-4 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 items-center">
            <div className="flex items-center gap-4 sm:gap-6">
              <img
                src={nadyhololLogo}
                alt="holol Club logo"
                className="w-24 h-24 sm:w-28 sm:h-28 object-contain shrink-0"
              />
              <div className="space-y-6">
                <h2 className="text-3xl sm:text-5xl font-headline font-extrabold text-white">
                  {t('contactBanner.title')}
                </h2>
                <p className="text-xl text-white/70">{t('contactBanner.subtitle')}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a
                href={`mailto:${t('contactBanner.emailValue')}`}
                className="rounded-xl bg-white/10 border border-white/20 p-6 backdrop-blur-md hover:border-secondary transition-colors flex flex-col items-center text-center"
              >
                <p className="text-sm text-secondary font-bold mb-2">{t('contactBanner.emailLabel')}</p>
                <p className="text-white font-semibold break-all">{t('contactBanner.emailValue')}</p>
              </a>
              <a
                href={`tel:${t('contactBanner.phoneValue').replace(/\s+/g, '')}`}
                className="rounded-xl bg-white/10 border border-white/20 p-6 backdrop-blur-md hover:border-secondary transition-colors flex flex-col items-center text-center"
              >
                <p className="text-sm text-secondary font-bold mb-2">{t('contactBanner.phoneLabel')}</p>
                <p dir="ltr" className="text-white font-semibold text-center [unicode-bidi:isolate] break-all">
                  {t('contactBanner.phoneValue')}
                </p>
              </a>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* Footer */}
      <footer className="bg-[#000c1e] dark:bg-[#000c1e] w-full pt-10 pb-6">
        <FadeIn>
          <div className="px-4 sm:px-12 max-w-screen-2xl mx-auto font-['Work_Sans'] text-sm uppercase tracking-widest">
            <div className="mx-auto w-fit text-center">
              <span className="text-xl font-black text-white mb-4 block">holol Club</span>
              <p className="text-white/40 lowercase normal-case tracking-normal mb-8">
                {t('hero.slogan2')}
              </p>
              <div className="flex gap-4 justify-center">
                <a
                  className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-secondary hover:border-secondary transition-all"
                  href="https://www.adhamelsharkawy.com/"
                  aria-label={t('footer.ariaWebsite')}
                  rel="noopener noreferrer"
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    public
                  </span>
                </a>
                {/*<a*/}
                {/*  className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-secondary hover:border-secondary transition-all"*/}
                {/*  href="https://holol.club"*/}
                {/*  aria-label={t('footer.ariaShare')}*/}
                {/*  rel="noopener noreferrer"*/}
                {/*>*/}
                {/*  <span className="material-symbols-outlined" aria-hidden="true">*/}
                {/*    share*/}
                {/*  </span>*/}
                {/*</a>*/}
                <a
                  className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-secondary hover:border-secondary transition-all"
                  href="mailto:contact@holol.club"
                  aria-label={t('footer.ariaEmail')}
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    mail
                  </span>
                </a>
                <a
                  className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-secondary hover:border-secondary transition-all"
                  href="https://www.linkedin.com/company/hololclub"
                  aria-label={t('footer.ariaLinkedIn')}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                    <path d="M6.94 8.5H3.56V20h3.38V8.5Zm.22-3.56C7.15 3.77 6.22 3 5.25 3s-1.9.77-1.9 1.94.91 1.94 1.87 1.94h.03c.99 0 1.91-.77 1.91-1.94ZM20.44 13.41c0-3.08-1.64-4.51-3.84-4.51-1.77 0-2.56.97-3 1.65V8.5h-3.38c.04 1.36 0 11.5 0 11.5h3.38v-6.42c0-.34.02-.68.12-.92.27-.68.89-1.39 1.93-1.39 1.36 0 1.9 1.04 1.9 2.57V20h3.38v-6.59Z"/>
                  </svg>
                </a>
                <a
                  className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-secondary hover:border-secondary transition-all"
                  href="https://www.youtube.com/@adhamelsharkaawy"
                  aria-label={t('footer.ariaYouTube')}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                    <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.1 31.1 0 0 0 0 12a31.1 31.1 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.1 31.1 0 0 0 24 12a31.1 31.1 0 0 0-.5-5.8ZM9.6 15.6V8.4L15.8 12l-6.2 3.6Z"/>
                  </svg>
                </a>
                <a
                  className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-secondary hover:border-secondary transition-all"
                  href="https://x.com/dradhambis"
                  aria-label={t('footer.ariaTwitter')}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                    <path d="M18.9 2H22l-6.8 7.8L23.2 22h-6.2l-4.9-6.4L6.5 22H3.4l7.2-8.2L1 2h6.3l4.4 5.8L18.9 2Zm-1.1 18h1.7L6.3 3.9H4.5L17.8 20Z"/>
                  </svg>
                </a>
                <a
                  className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-secondary hover:border-secondary transition-all"
                  href="https://www.facebook.com/dr.adhamelsharkaawy/"
                  aria-label={t('footer.ariaFacebook')}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                    <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.85c0-2.5 1.49-3.88 3.77-3.88 1.09 0 2.23.2 2.23.2v2.46h-1.26c-1.24 0-1.62.77-1.62 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12Z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </FadeIn>
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-12 mt-10 pt-6 border-t border-white/5 text-center">
          <p className="text-white/40">
            © {new Date().getFullYear()} {t('footer.copyright')}
          </p>
        </div>
      </footer>

      <AIChatWidget />

      <a
        href="https://wa.me/+447832180705"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contact us on WhatsApp"
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-[#25D366] text-white shadow-[0_12px_28px_rgba(37,211,102,0.4)] flex items-center justify-center hover:scale-105 transition-transform"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current" aria-hidden="true">
          <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .17 5.33.17 11.89c0 2.1.55 4.15 1.59 5.95L0 24l6.33-1.66a11.86 11.86 0 0 0 5.73 1.47h.01c6.56 0 11.89-5.33 11.89-11.89 0-3.18-1.24-6.16-3.44-8.44Zm-8.45 18.3h-.01a9.8 9.8 0 0 1-4.99-1.37l-.36-.21-3.76.99 1-3.66-.24-.38a9.83 9.83 0 0 1-1.51-5.26c0-5.43 4.42-9.85 9.86-9.85a9.8 9.8 0 0 1 6.97 2.89 9.8 9.8 0 0 1 2.88 6.96c0 5.44-4.42 9.86-9.85 9.86Zm5.4-7.36c-.3-.15-1.77-.88-2.04-.98-.27-.1-.47-.15-.67.15-.2.3-.77.98-.94 1.18-.17.2-.35.22-.64.08-.3-.15-1.24-.45-2.36-1.43-.88-.78-1.47-1.74-1.64-2.03-.17-.3-.02-.45.13-.6.13-.13.3-.35.44-.52.15-.18.2-.3.3-.5.1-.2.05-.38-.03-.53-.08-.15-.67-1.62-.92-2.22-.24-.58-.48-.5-.67-.5h-.57c-.2 0-.52.08-.8.38-.27.3-1.04 1.01-1.04 2.46 0 1.45 1.07 2.86 1.22 3.06.15.2 2.1 3.2 5.08 4.48.71.31 1.27.5 1.7.64.72.23 1.38.2 1.9.12.58-.09 1.77-.72 2.02-1.41.25-.7.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35Z" />
        </svg>
      </a>
    </>
  );
}

export default App;